/**
 * @file     upcomingOpenHouses.ts
 * @purpose  Filters open houses that have already ended, keyed on a caller-supplied
 *           clock so a client component can render the server's list byte-identically
 *           during hydration (now = null) and prune only after mount.
 */

import { daysUntilOpenHouse, openHouseWallClock, siteWallClockNow, type OpenHouseLike } from './openHouseTime';

/**
 * Returns the open houses that have not ended as of `now`. With `now = null`
 * the list is returned untouched: SSR and the hydration render must agree on
 * the server's (possibly CDN-stale) list, and only a post-mount clock may
 * drop entries. The no-end-time fallback expires at the end of the event's
 * calendar day in site time, never in the viewer's local zone.
 */
export function upcomingOpenHouses<T extends OpenHouseLike>(openHouses: T[], now: Date | null): T[] {
  if (!now) return openHouses;
  return openHouses.filter((oh) => !hasEnded(oh, now));
}

function hasEnded(oh: OpenHouseLike, now: Date): boolean {
  // Both sides as Vancouver wall clock: a zone-less end time is site-local,
  // and new Date() would read it in the viewer's zone.
  const end = openHouseWallClock(oh.endTime);
  const current = siteWallClockNow(now);
  if (end && current) return end < current;
  const days = daysUntilOpenHouse(oh.date, now);
  return days != null && days < 0;
}
